import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Check, Download, ShieldCheck } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { api, fmtPrice } from "@/lib/api";
import { usePageTitle, MANIFESTO, FAQS } from "@/lib/content";
import { Reveal } from "@/components/site/Reveal";
import { Marquee } from "@/components/site/Marquee";
import { PackMockup } from "@/components/site/PackMockup";
import { ProductCard } from "@/components/site/ProductCard";
import { CheckoutDialog } from "@/components/site/CheckoutDialog";

const HERO_IMG =
  "https://images.unsplash.com/photo-1786209273552-3b57dec47c94?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4A058R8MHwxfHNlYXJjaHwxfHxvcGVuJTIwcGl0JTIwbWluaW5nJTIwc2l0ZSUyMGhlYXZ5JTIwbWFjaGluZXJ5JTIwdHJ1Y2slMjBpcm9uJTIwb3JlfGVufDB8fHx8MTc5MDA1ODIyOXww&ixlib=rb-4.1.0&q=70&w=1600&auto=format";

function HeroLine({ children, delay }) {
  return (
    <div className="overflow-hidden">
      <motion.span
        className="block"
        initial={{ y: "112%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </div>
  );
}

export default function Home() {
  usePageTitle(
    "Sitebound — Construction Job Seeker Packs Australia",
    "Get job-ready for Australian construction, civil, mining and FIFO work. Resume templates, tickets & licences guidance, interview prep and site basics. Instant download."
  );
  const [products, setProducts] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const featured = products.find((p) => p.featured);
  const comingSoon = products.filter((p) => p.status === "coming_soon");

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="grain relative overflow-hidden">
        <motion.div className="absolute inset-0" style={{ y: imgY }}>
          <img
            src={HERO_IMG}
            alt="Australian open pit mining site with heavy machinery"
            className="h-[120%] w-full scale-110 object-cover"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0B0D]/80 via-[#0A0B0D]/72 to-[#0A0B0D]" />
        <div className="blueprint-grid absolute inset-0" />

        <div className="relative mx-auto flex min-h-[92vh] max-w-7xl flex-col justify-center px-4 py-24 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="mb-8 flex items-center gap-3"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#FF5500] opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#FF5500]" />
            </span>
            <span className="font-mono-ind text-[11px] uppercase tracking-[0.25em] text-slate-300">
              Australia-wide access — 2026
            </span>
          </motion.div>

          <h1 className="font-display text-[17vw] font-extrabold uppercase leading-[0.85] tracking-tight text-slate-100 sm:text-8xl lg:text-[9rem]">
            <HeroLine delay={0.1}>Get</HeroLine>
            <HeroLine delay={0.22}>
              <span className="text-outline">Site-</span>
            </HeroLine>
            <HeroLine delay={0.34}>
              <span className="text-[#FF5500]">Ready.</span>
            </HeroLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
          >
            Premium job seeker packs for Australian construction, civil, mining and FIFO work.
            Resumes, tickets guidance, interview prep and site basics — everything between you and
            your first day on the tools.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            className="mt-10 flex flex-col gap-4 sm:flex-row"
          >
            <Link
              data-testid="hero-cta-pack"
              to="/product/complete-construction-job-seeker-pack"
              className="group flex items-center justify-center gap-3 bg-[#FF5500] px-8 py-4 text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#E04800]"
            >
              Get the Complete Pack — $49 AUD
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              data-testid="hero-cta-shop"
              to="/shop"
              className="flex items-center justify-center gap-3 border border-white/25 px-8 py-4 text-sm font-bold uppercase tracking-widest text-slate-100 transition-colors hover:border-[#FF5500] hover:text-[#FF5500]"
            >
              Browse the range
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.95 }}
            className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-400"
          >
            <span className="flex items-center gap-2">
              <Download className="h-3.5 w-3.5 text-[#FF5500]" /> Instant download
            </span>
            <span className="flex items-center gap-2">
              <ShieldCheck className="h-3.5 w-3.5 text-[#FF5500]" /> Secure card payment
            </span>
            <span className="font-mono-ind uppercase tracking-widest">Apple Pay · Google Pay</span>
          </motion.div>
        </div>
      </section>

      <Marquee />

      {/* FEATURED PRODUCT */}
      {featured && (
        <section data-testid="featured-pack-section" className="relative overflow-hidden py-20 md:py-28">
          <div className="spotlight absolute inset-0" />
          <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
            <Reveal>
              <PackMockup />
            </Reveal>
            <div>
              <Reveal>
                <div className="mono-label">The flagship pack</div>
                <h2 className="font-display mt-4 text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-slate-100 sm:text-5xl lg:text-6xl">
                  Complete Construction Job Seeker Pack
                </h2>
                <p className="mt-6 max-w-lg text-base leading-relaxed text-slate-400">
                  {featured.description}
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <ul className="mt-8 space-y-3">
                  {featured.components.slice(0, 5).map((c) => (
                    <li key={c.name} className="flex items-start gap-3 text-sm text-slate-300">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#FF5500]" />
                      {c.name}
                    </li>
                  ))}
                  <li className="font-mono-ind pl-7 text-xs uppercase tracking-widest text-slate-500">
                    + {featured.components.length - 5} more sections
                  </li>
                </ul>
              </Reveal>
              <Reveal delay={0.18}>
                <div className="mt-10 flex flex-wrap items-center gap-6">
                  <div>
                    <span className="font-display text-4xl font-extrabold text-[#FF5500]">
                      {fmtPrice(featured.price)}
                    </span>
                    <span className="ml-3 text-lg text-slate-500 line-through">
                      {fmtPrice(featured.compare_at)}
                    </span>
                  </div>
                  <div className="flex gap-3">
                    <CheckoutDialog
                      product={featured}
                      trigger={
                        <button
                          data-testid="featured-buy-button"
                          className="bg-[#FF5500] px-7 py-3.5 text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#E04800]"
                        >
                          Buy now
                        </button>
                      }
                    />
                    <Link
                      data-testid="featured-details-link"
                      to={`/product/${featured.slug}`}
                      className="border border-white/25 px-7 py-3.5 text-sm font-bold uppercase tracking-widest text-slate-100 transition-colors hover:border-[#FF5500] hover:text-[#FF5500]"
                    >
                      What's inside
                    </Link>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </section>
      )}

      {/* FOUNDER / TRUST */}
      <section data-testid="credibility-section" className="border-t border-white/10 py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="flex flex-col items-start gap-8 sm:flex-row sm:items-center sm:gap-10">
              <img
                src="/jack-founder.jpg"
                alt="Jack, founder of Sitebound"
                data-testid="founder-photo"
                width="160"
                height="160"
                loading="lazy"
                className="h-36 w-36 shrink-0 rounded-sm border border-white/10 object-cover sm:h-40 sm:w-40"
              />
              <div className="max-w-2xl">
                <div className="mono-label">The founder</div>
                <h2 className="font-display mt-4 text-3xl font-extrabold uppercase leading-[0.95] tracking-tight text-slate-100 sm:text-4xl">
                  Built from real recruitment experience
                </h2>
                <p className="mt-5 text-base leading-relaxed text-slate-300">
                  Sitebound was created by Jack, a civil and construction recruitment professional
                  with 1,000+ successful job placements and a current ranking among the top 10
                  recruiters in Western Australia on SEEK.
                </p>
                <p className="mt-4 text-sm leading-relaxed text-slate-400">
                  After years of seeing where job seekers struggle, from resumes and applications to
                  tickets, interviews and getting that first opportunity, Jack created Sitebound to
                  bring practical resources together in one place.
                </p>
                <Link
                  data-testid="founder-meet-link"
                  to="/about"
                  className="mt-6 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-[#FF5500] transition-colors hover:text-white"
                >
                  Meet Jack <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* MANIFESTO */}
      <section data-testid="manifesto-section" className="border-t border-white/10 bg-[#0D0F12] py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="mono-label">Why Sitebound exists</div>
            <h2 className="font-display mt-4 max-w-3xl text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-slate-100 sm:text-5xl">
              The industry needs workers. You need a way in.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 gap-px bg-white/10 md:grid-cols-3">
            {MANIFESTO.map((chapter, i) => (
              <Reveal key={chapter.num} delay={i * 0.1} className="bg-[#0D0F12]">
                <div className="h-full p-8 md:p-10">
                  <div className="font-display text-outline text-6xl font-extrabold">{chapter.num}</div>
                  <h3 className="font-display mt-6 text-2xl font-bold uppercase tracking-tight text-slate-100">
                    {chapter.title}
                  </h3>
                  <p className="mt-4 text-sm leading-relaxed text-slate-400">{chapter.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* COMING SOON RANGE */}
      <section data-testid="range-section" className="py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <div className="mono-label">The range</div>
              <h2 className="font-display mt-4 text-4xl font-extrabold uppercase tracking-tight text-slate-100 sm:text-5xl">
                More packs in production
              </h2>
            </div>
            <Link
              data-testid="range-view-all"
              to="/shop"
              className="flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-[#FF5500]"
            >
              View full range <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {comingSoon.slice(0, 3).map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.08}>
                <ProductCard product={p} index={i + 1} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ TEASER */}
      <section className="border-t border-white/10 bg-[#0D0F12] py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 sm:px-6 lg:grid-cols-5 lg:px-8">
          <Reveal className="lg:col-span-2">
            <div className="mono-label">Questions</div>
            <h2 className="font-display mt-4 text-4xl font-extrabold uppercase tracking-tight text-slate-100 sm:text-5xl">
              Straight answers
            </h2>
            <p className="mt-5 text-sm leading-relaxed text-slate-400">
              Digital delivery, what's included, refunds, tickets — the things people actually ask
              before they buy.
            </p>
            <Link
              data-testid="faq-teaser-link"
              to="/faq"
              className="mt-7 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-[#FF5500]"
            >
              All FAQs <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-3">
            <Accordion type="single" collapsible className="w-full">
              {FAQS.slice(0, 4).map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-white/10">
                  <AccordionTrigger
                    data-testid={`home-faq-trigger-${i}`}
                    className="text-left text-sm font-semibold uppercase tracking-wide text-slate-200 hover:text-[#FF5500] hover:no-underline"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-slate-400">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="relative overflow-hidden">
        <div className="hazard-stripes h-1.5 w-full" />
        <div className="blueprint-grid bg-[#FF5500] py-16 md:py-20">
          <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-4 sm:px-6 md:flex-row md:items-center lg:px-8">
            <h2 className="font-display max-w-2xl text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-[#0A0B0D] sm:text-5xl lg:text-6xl">
              Back yourself. Get site-ready.
            </h2>
            <Link
              data-testid="cta-band-button"
              to="/product/complete-construction-job-seeker-pack"
              className="shrink-0 bg-[#0A0B0D] px-8 py-4 text-sm font-bold uppercase tracking-widest text-white transition-transform duration-300 hover:-translate-y-0.5"
            >
              Get the pack — $49 AUD
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
